import React, { ReactNode } from 'react';
import AntdPopover from 'antd/lib/popover';

/**
 * {@docCategory Popover}
 */
export type PopoverPlacement =
   | 'top'
   | 'left'
   | 'right'
   | 'bottom'
   | 'topLeft'
   | 'topRight'
   | 'bottomLeft'
   | 'bottomRight'
   | 'leftTop'
   | 'leftBottom'
   | 'rightTop'
   | 'rightBottom';

/**
 * {@docCategory Popover}
 */
export declare type PopoverRenderFunction = () => React.ReactNode;

/**
 * {@docCategory Popover}
 */
export interface PopoverProps {
   /**
    * Title of the card
    */
   title?: ReactNode | PopoverRenderFunction;

   /**
    * Content of the card
    */
   content?: ReactNode | PopoverRenderFunction;

   /**
    * Whether the arrow is pointed at the center of target
    */
   arrowPointAtCenter?: boolean;

   /**
    * Whether to adjust popup placement automatically when popup is off screen
    */
   autoAdjustOverflow?: boolean;

   /**
    * The background color
    */
   backgroundColor?: string;

   /**
    * The position of the tooltip relative to the target
    */
   placement?: PopoverPlacement;

   /**
    * Tooltip trigger mode. Could be multiple by passing an array
    */
   trigger?: 'hover' | 'focus' | 'click' | 'contextMenu' | Array<string>;

   /**
    * Whether the floating tooltip card is visible or not
    */
   visible?: boolean;

   /**
    * Callback executed when visibility of the tooltip card is changed
    */
   onVisibleChange?: (visible: boolean) => void;

   /**
    * The DOM container of the tip, the default behavior is to create a div element in body
    */
   getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

   /**
    * Class name of the tooltip card
    */
   overlayClassName?: string;

   /**
    * Whether destroy tooltip when hidden, parent container of tooltip will be destroyed when keepParent is false
    */
   destroyPopoverOnHide?: boolean | {
      keepParent?: boolean;
   };
}

export const Popover = (props: React.PropsWithChildren<PopoverProps>) => {
   const visibleProp = props.visible === undefined ? {} : {visible: props.visible}
   return (
      <AntdPopover
         title={props.title}
         content={props.content}
         arrowPointAtCenter={props.arrowPointAtCenter}
         autoAdjustOverflow={props.autoAdjustOverflow}
         color={props.backgroundColor}
         placement={props.placement}
         trigger={props.trigger}
         {...visibleProp}
         onVisibleChange={props.onVisibleChange}
         getPopupContainer={props.getPopupContainer}
         overlayClassName={props.overlayClassName}
         destroyTooltipOnHide={props.destroyPopoverOnHide}
      >
         {props.children}
      </AntdPopover>
   );
};
